import { type FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, Overlay, Popover } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import './styles/styles.css';
import { DateTime } from 'luxon';
import CheckRadioIcon from '../../../assets/check-radio-icon.svg';
import DateIcon from '../../../assets/date.svg';
import DropDownIcon from '../../../assets/drop-down-icon.svg';
import DropDownOpenIcon from '../../../assets/drop-down-open.svg';
import ExpertIcon from '../../../assets/expert.svg';
import UnCheckRadioIcon from '../../../assets/uncheck-radio-icon.svg';
import { monthList, yearList } from '../../../constants/generalData';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import {
  DocumentExportType,
  FieldTextAlignment,
  PDFPageOrientation,
  PDFPageSizes,
} from '../../../modules/enums/types';
import { useAuth, useLoading, useRequest } from '../../../modules/hooks';
import type {
  ReservationsReport,
  ReservationsReportResponse,
  ReservationsReportSummary,
} from '../../../modules/interfaces/reports';
import { endpoints } from '../../../modules/mappers/urls';
import { formatCurrency } from '../../../modules/utils/formatCurrency';
import {
  type CustomFormat,
  type ExportOptions,
  ReportExporter,
} from '../../../modules/utils/ReportExporter';
import Header from '../../Header';
import DeleteModal from '../../Modals/DeleteModal/DeleteModal';
import ReportExportOptions from '../../Modals/ReportExportOptions';
import { ReservationsReportTable } from '../../Tables/ReservationsReports';

function getMonthNumber(monthName: string): number | null {
  const dt = DateTime.fromFormat(monthName, 'MMMM', { locale: 'en' });
  return dt.isValid ? dt.month : null;
}

export const ReservationsReports: FC = () => {
  const { credentialsInfo } = useAuth()!;
  const [modal, setModal] = useState<boolean>(false);
  const [reportData, setReportData] = useState<ReservationsReport[]>([]);
  const [reportDataSummary, setReportDataSummary] = useState<ReservationsReportSummary>({
    sales_addons: 0,
    total_addons: 0,
    taxes: 0,
    total_rooms: 0,
    total: 0,
  });
  const { selectedProperty } = useContext(UserPropertiesContext)!;
  const { setLoading } = useLoading()!;
  const [view, setView] = useState<string>('by Months');
  const [viewFinal, setViewFinal] = useState<string>('by Months');
  const viewArray = ['by Quarter', 'by Months'];
  const [dateShow, setDateShow] = useState(false);
  const dateTarget = useRef(null);
  const [viewShow, setViewShow] = useState(false);
  const viewTarget = useRef(null);
  const fromMonth = useRef(null);
  const toMonth = useRef(null);
  const fromYear = useRef(null);
  const toYear = useRef(null);
  const [fromMonthShow, setFromMonthShow] = useState(false);
  const [toMonthShow, setToMonthShow] = useState(false);
  const [fromYearShow, setFromYearShow] = useState(false);
  const [toYearShow, setToYearShow] = useState(false);
  const [fromMonthValue, setFromMonthValue] = useState('January');
  const [toMonthValue, setToMonthValue] = useState('December');
  const [fromYearValue, setFromYearValue] = useState(
    DateTime.now().minus({ years: 3 }).year.toString(),
  );
  const [toYearValue, setToYearValue] = useState(DateTime.now().year.toString());
  const [fromMonthValueFinal, setFromMonthValueFinal] = useState<string>('January');
  const [toMonthValueFinal, setToMonthValueFinal] = useState<string>('December');
  const [fromYearValueFinal, setFromYearValueFinal] = useState<string>(
    DateTime.now().minus({ years: 3 }).year.toString(),
  );
  const [toYearValueFinal, setToYearValueFinal] = useState<string>(
    DateTime.now().year.toString(),
  );
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [selectedExportType, setSelectedExportType] = useState<DocumentExportType>(
    DocumentExportType.PDF,
  );
  const [isExportInProgress, setIsExportInProgress] = useState(false);

  const getReservationsReportUrl = () => {
    const urlQueriesValues = {
      periodType: viewFinal === 'by Months' ? 'month' : 'quarter',
      yearFrom: fromYearValueFinal,
      yearTo: toYearValueFinal,
      monthFrom: getMonthNumber(fromMonthValueFinal),
      monthTo: getMonthNumber(toMonthValueFinal),
    };
    const url = `${endpoints.REPORTS}/reservations?${Object.entries(
      urlQueriesValues,
    ).reduce((acc, [key, value]) => {
      if (value) {
        return `${acc}${key}=${value}&`;
      }
      return acc;
    }, '')}`;
    return url;
  };

  const [
    { data: reservationReportResult, loading: loadingReservationsReport },
    getReservationsReport,
  ] = useRequest<ReservationsReportResponse>(getReservationsReportUrl(), 'get', {
    authToken: credentialsInfo?.token,
    propertyId: selectedProperty?.property.uuid || undefined,
  });

  const refreshHandler = useCallback(async () => {
    console.log('test');
  }, []);

  const onViewItemClick = (reportItem: string) => {
    setView(reportItem);
  };

  const renderViewItem = (reportItem: string) => {
    const isSelected = view === reportItem;
    return (
      <div
        className="drop-down-status-item"
        style={{ backgroundColor: isSelected ? '#F6F6F6' : '#FFFFFF' }}
        onClick={() => onViewItemClick(reportItem)}
      >
        <img
          className="checkbox-icon"
          src={isSelected ? CheckRadioIcon : UnCheckRadioIcon}
          alt="check-icon"
        />
        <div style={{ textTransform: 'none' }}>{reportItem}</div>
      </div>
    );
  };

  const onCancleView = () => {
    setView(viewFinal);
    setViewShow(false);
  };

  const onApplyView = () => {
    if (view) {
      setViewFinal(view);
      setViewShow(false);
    }
  };

  const onCancledate = () => {
    setDateShow(false);
    setFromMonthValue(fromMonthValueFinal);
    setToMonthValue(toMonthValueFinal);
    setFromYearValue(fromYearValueFinal);
    setToYearValue(toYearValueFinal);
  };

  const onApplyDate = () => {
    setDateShow(false);
    if (fromMonthValue) {
      setFromMonthValueFinal(fromMonthValue);
    }
    if (toMonthValue) {
      setToMonthValueFinal(toMonthValue);
    }
    if (fromYearValue) {
      setFromYearValueFinal(fromYearValue);
    }
    if (toYearValue) {
      setToYearValueFinal(toYearValue);
    }
  };

  const onFromMonthClick = () => {
    setFromMonthShow(true);
  };

  const onFromYearClick = () => {
    setFromYearShow(true);
  };

  const onToMonthClick = () => {
    setToMonthShow(true);
  };

  const onToYearClick = () => {
    setToYearShow(true);
  };

  const onMonthClick = (isFrom: boolean, month: string) => {
    setFromMonthShow(false);
    setToMonthShow(false);
    if (isFrom) {
      setFromMonthValue(month);
    } else {
      setToMonthValue(month);
    }
  };

  const onYearClick = (isFrom: boolean, year: string) => {
    setFromYearShow(false);
    setToYearShow(false);
    if (isFrom) {
      setFromYearValue(year);
    } else {
      setToYearValue(year);
    }
  };

  const renderDateByYear = () => {
    return (
      <div className="drop-down-container-status">
        <div className="artical-dropdown-container">
          <div className="date-text-main-container">
            <div className="date-text-container ">
              <div className="from-and-to-text-style">From</div>
              <div
                onClick={onFromYearClick}
                ref={fromYear}
                className="year-calender-container "
              >
                <div className="year-text-style">{fromYearValue}</div>
                <img
                  className="drop-down-icon-style"
                  src={fromYearShow ? DropDownOpenIcon : DropDownIcon}
                  alt="drop-down"
                />
              </div>
            </div>
          </div>
          <div className="date-text-main-container">
            <div className="date-text-container ">
              <div className="from-and-to-text-style">To</div>
              <div
                onClick={onToYearClick}
                ref={toYear}
                className="year-calender-container "
              >
                <div className="year-text-style">{toYearValue}</div>
                <img
                  className="drop-down-icon-style"
                  src={toYearShow ? DropDownOpenIcon : DropDownIcon}
                  alt="drop-down"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="apply-and-cancle-container">
          <div onClick={onCancledate} className="cancle-text-container">
            Cancel
          </div>
          <div onClick={onApplyDate} className="apply-text-container">
            Apply
          </div>
        </div>
      </div>
    );
  };

  const renderMonthList = (isFrom: boolean) => {
    return (
      <div style={{ overflowY: 'scroll', maxHeight: '50vh' }}>
        {monthList.map((item) => (
          <div
            onClick={() => onMonthClick(isFrom, item)}
            className="dropdown-item-calendar-picker"
          >
            {item}
          </div>
        ))}
      </div>
    );
  };

  const renderYearList = (isFrom: boolean) => {
    return (
      <div style={{ overflowY: 'scroll', maxHeight: '50vh' }}>
        {yearList.map((item) => (
          <div
            onClick={() => onYearClick(isFrom, item.toString())}
            className="dropdown-item-calendar-picker"
          >
            {item}
          </div>
        ))}
      </div>
    );
  };

  const renderDateByMonth = () => {
    return (
      <div className="drop-down-container-status">
        <div className="artical-dropdown-container">
          <div className="date-text-main-container">
            <div className="date-text-container ">
              <div className="from-and-to-text-style">From</div>
              <div
                ref={fromMonth}
                className="year-calender-container by-month-container"
                onClick={() => onFromMonthClick()}
              >
                <div className="year-text-style">{fromMonthValue}</div>
                <img
                  className="drop-down-icon-style"
                  src={fromMonthShow ? DropDownOpenIcon : DropDownIcon}
                  alt="drop-down"
                />
              </div>
            </div>
          </div>
          <div className="date-text-main-container">
            <div className="date-text-container ">
              <div
                onClick={onFromYearClick}
                ref={fromYear}
                className="year-calender-container by-month-text"
              >
                <div className="year-text-style">{fromYearValue}</div>
                <img
                  className="drop-down-icon-style"
                  src={fromYearShow ? DropDownOpenIcon : DropDownIcon}
                  alt="drop-down"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="artical-dropdown-container">
          <div className="date-text-main-container">
            <div className="date-text-container ">
              <div className="from-and-to-text-style">To</div>
              <div
                ref={toMonth}
                onClick={() => onToMonthClick()}
                className="year-calender-container by-month-container "
              >
                <div className="year-text-style">{toMonthValue}</div>
                <img
                  className="drop-down-icon-style"
                  src={toMonthShow ? DropDownOpenIcon : DropDownIcon}
                  alt="drop-down"
                />
              </div>
            </div>
          </div>
          <div className="date-text-main-container">
            <div className="date-text-container ">
              <div
                onClick={onToYearClick}
                ref={toYear}
                className="year-calender-container by-month-text"
              >
                <div className="year-text-style">{toYearValue}</div>
                <img
                  className="drop-down-icon-style"
                  src={toYearShow ? DropDownOpenIcon : DropDownIcon}
                  alt="drop-down"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="apply-and-cancle-container">
          <div onClick={onCancledate} className="cancle-text-container">
            Cancel
          </div>
          <div onClick={onApplyDate} className="apply-text-container">
            Apply
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (reservationReportResult) {
      setReportData(reservationReportResult.result.rows);
      setReportDataSummary(reservationReportResult.result.summary);
    }
  }, [reservationReportResult]);

  useEffect(() => {
    const isLoading = loadingReservationsReport;
    setLoading(isLoading);
  }, [loadingReservationsReport, setLoading]);

  const exportReport = async () => {
    if (reportData.length > 0) {
      const customFormat = new Map<string, CustomFormat>([
        [
          'reservationId',
          {
            parseCurrency: false,
            textAlign: FieldTextAlignment.LEFT,
            replace: ['-', ' '],
          },
        ],
        [
          'arrivalDate',
          {
            parseCurrency: false,
            textAlign: FieldTextAlignment.LEFT,
          },
        ],
        [
          'departureDate',
          {
            parseCurrency: false,
            textAlign: FieldTextAlignment.LEFT,
          },
        ],
        [
          'sales_addons',
          {
            parseCurrency: false,
            textAlign: FieldTextAlignment.CENTER,
          },
        ],
      ]);
      const exportOptions: ExportOptions<ReservationsReport> = {
        reportName: 'Reservations Report',
        subtitle: `From ${fromMonthValueFinal}, ${fromYearValueFinal} to ${toMonthValueFinal}, ${toYearValueFinal}`,
        hiddenFields: ['propertyId', 'year', 'month', 'quarter'],
        fixedStartFields: [
          'reservationId',
          'arrivalDate',
          'departureDate',
          'guestName',
          'roomName',
          'sales_addons',
          'total_addons',
          'total_rooms',
          'taxes',
          'total',
        ],
        customFieldNames: {
          reservationId: 'RESERVATION ID',
          arrivalDate: 'ARRIVAL',
          departureDate: 'DEPARTURE',
          guestName: 'GUEST',
          roomName: 'ROOM',
          sales_addons: 'ADDONS SOLD',
          total_addons: 'ADDONS REVENUE',
          total_rooms: 'ROOM REVENUE',
          taxes: 'TAXES',
          total: 'TOTAL',
        },
        pdfPageSize: PDFPageSizes.LEGAL,
        pdfPageOrientation: PDFPageOrientation.LANDSCAPE,
        customFormat,
      };

      const exporter = new ReportExporter<ReservationsReport>(reportData, exportOptions);
      await exporter.export(selectedExportType);
      setIsExportInProgress(false);
    }
  };

  const startExport = () => {
    setIsExportInProgress(true);
    setShowExportOptions(false);
    exportReport();
  };

  const onExportReport = () => {
    setShowExportOptions(true);
  };

  return (
    <Container fluid className="article-page-container p-0">
      <Header title="Reservations Reports" />
      <div className="artical-text-main-container">
        <div className="artical-dropdown-container ">
          <div
            ref={dateTarget}
            onClick={() => setViewShow(!viewShow)}
            className="category-text-container"
          >
            <div className="category-text-style">
              <div className="view-text-style">View: </div>
              {viewFinal}
            </div>
            <img src={viewShow ? DropDownOpenIcon : DropDownIcon} alt="drop-down" />
          </div>
          <div className="catagory-container">
            <div
              ref={viewTarget}
              onClick={() => setDateShow(!dateShow)}
              className="date-rage-picker-text-container"
            >
              <img src={DateIcon} alt="date" />
              <div className="category-text-style">
                {viewFinal === 'by Months'
                  ? `${fromMonthValueFinal}, ${fromYearValueFinal} — ${toMonthValueFinal}, ${toYearValueFinal}`
                  : `${fromYearValueFinal} — ${toYearValueFinal}`}
              </div>
              <img
                className="drop-down-icon-style"
                src={dateShow ? DropDownOpenIcon : DropDownIcon}
                alt="drop-down"
              />
            </div>
          </div>
        </div>
        <Button
          className="add-artical-container"
          onClick={onExportReport}
          {...(isExportInProgress && { disabled: true })}
        >
          <img src={ExpertIcon} alt="plus-icon" />
          <div className="add-artical-text-style">
            {isExportInProgress ? 'Exporting...' : 'Export'}
          </div>
        </Button>
      </div>

      <Overlay
        onHide={() => setDateShow(false)}
        target={viewTarget.current}
        show={dateShow}
        placement="bottom"
      >
        <Popover id="popover-positioned-bottom">
          <Popover.Body className="property_overlay">
            {viewFinal === 'by Months' ? renderDateByMonth() : renderDateByYear()}
          </Popover.Body>
        </Popover>
      </Overlay>
      <Overlay
        onHide={() => setViewShow(false)}
        target={dateTarget.current}
        rootClose
        show={viewShow}
        placement="bottom"
      >
        <Popover id="popover-positioned-bottom">
          <Popover.Body className="property_overlay">
            <div className="drop-down-container-status">
              {viewArray.map(renderViewItem)}
              <div className="apply-and-cancle-container">
                <div onClick={onCancleView} className="cancle-text-container">
                  Cancel
                </div>
                <div onClick={onApplyView} className="apply-text-container">
                  Apply
                </div>
              </div>
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>
      <Overlay
        onHide={() => setFromMonthShow(false)}
        target={fromMonth.current}
        rootClose
        show={fromMonthShow}
        placement="bottom"
      >
        <Popover id="popover-positioned-bottom">
          <Popover.Body className="property_overlay">
            {renderMonthList(true)}
          </Popover.Body>
        </Popover>
      </Overlay>
      <Overlay
        onHide={() => setToMonthShow(false)}
        target={toMonth.current}
        rootClose
        show={toMonthShow}
        placement="bottom"
      >
        <Popover id="popover-positioned-bottom">
          <Popover.Body className="property_overlay">
            {renderMonthList(false)}
          </Popover.Body>
        </Popover>
      </Overlay>
      <Overlay
        onHide={() => setFromYearShow(false)}
        target={fromYear.current}
        rootClose
        show={fromYearShow}
        placement="bottom"
      >
        <Popover id="popover-positioned-bottom">
          <Popover.Body className="property_overlay">{renderYearList(true)}</Popover.Body>
        </Popover>
      </Overlay>
      <Overlay
        onHide={() => setToYearShow(false)}
        target={toYear.current}
        rootClose
        show={toYearShow}
        placement="bottom"
      >
        <Popover id="popover-positioned-bottom">
          <Popover.Body className="property_overlay">
            {renderYearList(false)}
          </Popover.Body>
        </Popover>
      </Overlay>
      <ReservationsReportTable
        searchString=""
        tableData={reportData}
        refreshHandler={refreshHandler}
      />
      <DeleteModal
        show={modal}
        onDelete={() => setModal(false)}
        onHide={() => setModal(false)}
        title="Delete Article"
        description="Are you sure you want to delete this article?"
      />
      <ReportExportOptions
        show={showExportOptions}
        onHide={() => setShowExportOptions(false)}
        onStartExport={startExport}
        selectedExportType={selectedExportType}
        onSelectExportOption={(type: DocumentExportType) => setSelectedExportType(type)}
      />
      <div className="bottom-total-view">
        <div className="total-text-view">Total</div>
        <div className="whitecell"> </div>
        <div className="whitecell"> </div>
        <div className="whitecell"> </div>
        <div className="whitecell"> </div>
        <div className="ammount-text-style">{reportDataSummary.sales_addons}</div>
        <div className="ammount-text-style">
          {formatCurrency(reportDataSummary.total_addons)}
        </div>
        <div className="ammount-text-style">
          {formatCurrency(reportDataSummary.total_rooms)}
        </div>
        <div className="ammount-text-style">
          {formatCurrency(reportDataSummary.taxes)}
        </div>
        <div className="ammount-text-style">
          {formatCurrency(reportDataSummary.total)}
        </div>
      </div>
    </Container>
  );
};
